// Shared domain types — used by both the Worker and the React frontend.

export type UserRole = 'crew' | 'logistics' | 'admin';

export type FulfillmentChannel = 'grocery' | 'vendor';

/**
 * Which supply program a catalog item — and therefore an order — belongs to.
 * Kitchen and cleaning are ordered by different people out of different
 * catalogs, so they consolidate into separate orders and separate lists.
 *
 * The program of an *order* is always derived from `items.program` on its
 * lines, never from the request it came from. See `migrations/0006`.
 */
export type SupplyProgram = 'kitchen' | 'cleaning';

export const PROGRAM_LABELS: Record<SupplyProgram, string> = {
  kitchen: 'Kitchen',
  cleaning: 'Cleaning',
};

/**
 * Order lifecycle: draft → submitted → placed → received (+ cancelled).
 *
 * `'approved'` is **legacy**: the approval gate was removed (orders are hand-
 * delivered, so a separate approve click bought nothing). No transition leads
 * to it anymore, but the value stays in the union and in the D1 CHECK
 * constraint so any historical row still reads and can still move forward.
 */
export type OrderStatus = 'draft' | 'submitted' | 'approved' | 'placed' | 'received' | 'cancelled';

/**
 * User-facing status wording. `received` reads as "Delivered" — Dan drops the
 * order off, and that's the language the kitchen uses. The stored value stays
 * `received` (with received_by / received_at), so no migration is involved.
 */
export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
  placed: 'Placed',
  received: 'Delivered',
  cancelled: 'Cancelled',
};

export type RequestStatus = 'open' | 'consolidated' | 'cancelled';

/**
 * User-facing wording. `consolidated` reads as "Ordered": to whoever submitted
 * it, the meaningful fact is that it's been turned into an order, not the
 * internal bookkeeping step.
 */
export const REQUEST_STATUS_LABELS: Record<RequestStatus, string> = {
  open: 'Waiting',
  consolidated: 'Ordered',
  cancelled: 'Cancelled',
};

export type VendorSendMethod = 'email' | 'manual';

export interface Station {
  id: number;
  name: string;
  code: string | null;
  is_active: boolean;
}

export interface Category {
  id: number;
  name: string;
  sort_order: number;
  is_active: boolean;
  /** NULL when the category is shared by both programs. */
  program: SupplyProgram | null;
}

export interface Vendor {
  id: number;
  name: string;
  default_email: string | null;
  send_method: VendorSendMethod;
  notes: string | null;
  is_active: boolean;
}

export interface Item {
  id: number;
  name: string;
  category_id: number | null;
  program: SupplyProgram;
  fulfillment_channel: FulfillmentChannel;
  vendor_id: number | null;
  /** Vendor's own product code (Abel item #, WB Mason PEE code). Null off-catalog. */
  sku: string | null;
  unit: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Request {
  id: number;
  station_id: number;
  submitted_by: string | null;
  submitter_name: string | null;
  submitted_at: string;
  note: string | null;
  status: RequestStatus;
  /**
   * Which catalog the submitter was looking at. A hint for filtering and
   * reporting only — consolidation reads `items.program` off each line, so this
   * being NULL (as it is for every pre-0006 request) changes nothing.
   */
  program: SupplyProgram | null;
}

export interface RequestItem {
  id: number;
  request_id: number;
  item_id: number | null;
  item_name: string;
  qty_requested: number;
  note: string | null;
}

export interface Order {
  id: number;
  program: SupplyProgram;
  channel: FulfillmentChannel;
  vendor_id: number | null;
  status: OrderStatus;
  pick_list: string | null;
  recipient_email: string | null;
  note: string | null;
  created_by: string | null;
  created_at: string;
  approved_by: string | null;
  approved_at: string | null;
  placed_by: string | null;
  placed_at: string | null;
  received_by: string | null;
  received_at: string | null;
}

export interface OrderItem {
  id: number;
  order_id: number;
  item_id: number | null;
  item_name: string;
  qty: number;
  unit: string | null;
  /** CSV of the request ids that fed this line. Null when added during fulfillment. */
  source_request_ids: string | null;
  /** Why the line was changed — "store was out", "got 2L not 1L". */
  note: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

/**
 * Reserved for a future version — request photo attachments (R2) are deferred.
 * The `request_attachments` table exists as dormant schema; there is no upload
 * path (worker or frontend) wired yet.
 */
export interface RequestAttachment {
  id: number;
  request_id: number;
  filename: string;
  r2_key: string;
  content_type: string | null;
  size_bytes: number | null;
  uploaded_at: string;
}
